import { useContext, useRef } from "react";
import { brand } from "../../../context";
import { TranslationContext } from "../../../umbilical_tools";

export type QuotationFiltersType = {
  title?: string;
  created_from?: string;
  created_to?: string;
  offer_status?: "quotation_sent" | "offer_in_progress" | "offer_available" | "offer_accepted";
};

export type QuotationFiltersProps = {
  filters: QuotationFiltersType;
  onApply: (filters: QuotationFiltersType) => void;
  onReset: () => void;
};

export function QuotationFilters({ filters, onApply, onReset }: QuotationFiltersProps) {
  const { t } = useContext(TranslationContext) as { t: (key: string) => string };
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const cleanedValues: QuotationFiltersType = {};

    const title = formData.get("title") as string;
    if (title?.trim()) cleanedValues.title = title.trim();

    const created_from = formData.get("created_from") as string;
    if (created_from) cleanedValues.created_from = created_from;

    const created_to = formData.get("created_to") as string;
    if (created_to) cleanedValues.created_to = created_to;

    const offer_status = formData.get("offer_status") as string;
    if (offer_status && offer_status !== "") {
      cleanedValues.offer_status = offer_status as QuotationFiltersType["offer_status"];
    }

    onApply(cleanedValues);
  };

  const handleReset = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    onReset();
  };

  return (
    <div className="QuotationFilters p-4 bg-gray-50 rounded-lg mb-4 border border-gray-200">
      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="grid grid-rows-[auto_1fr] gap-1">
            <label htmlFor="title" className="text-sm font-medium text-gray-700">
              {t("title") || "Title"}
            </label>
            <input
              type="text"
              id="title"
              name="title"
              defaultValue={filters.title || ""}
              placeholder={t("searchByTitle") || "Search by title..."}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-rows-[auto_1fr] gap-1">
            <label htmlFor="created_from" className="text-sm font-medium text-gray-700">
              {t("createdFrom")}
            </label>
            <input
              type="date"
              id="created_from"
              name="created_from"
              defaultValue={filters.created_from || ""}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-rows-[auto_1fr] gap-1">
            <label htmlFor="created_to" className="text-sm font-medium text-gray-700">
              {t("createdTo")}
            </label>
            <input
              type="date"
              id="created_to"
              name="created_to"
              defaultValue={filters.created_to || ""}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="grid grid-rows-[auto_1fr] gap-1">
            <label htmlFor="offer_status" className="text-sm font-medium text-gray-700">
              {t("offerStatus") || "Offer Status"}
            </label>
            <select
              id="offer_status"
              name="offer_status"
              defaultValue={filters.offer_status || ""}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            >
              <option value="">{t("selectOfferStatus") || "All"}</option>
              <option value="quotation_sent">{t("quotationSent") || "Quotation Sent"}</option>
              <option value="offer_in_progress">
                {t("offerInProgress") || "Offer In Progress"}
              </option>
              <option value="offer_available">{t("offerAvailable") || "Offer Available"}</option>
              <option value="offer_accepted">{t("offerAccepted") || "Offer Accepted"}</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-[auto_auto] gap-4 justify-end">
          <button type="button" onClick={handleReset} className="Button" data-brand={brand}>
            {t("reset") || "Reset"}
          </button>
          <button type="submit" className="Button" data-brand={brand}>
            {t("apply") || "Apply"}
          </button>
        </div>
      </form>
    </div>
  );
}
